package raw.engine

import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import raw.models.CreditCard
import raw.models.RouteDefinition
import raw.models.Transaction
//import raw.models.TransactionRequest
import raw.models.UserState


/**
./gradlew jsBrowserDistribution
*/


@OptIn(ExperimentalJsExport::class)
@JsExport
object RewStackBridge {
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = true // Important for optional fields
        coerceInputValues = true
        isLenient = true
    }

    fun runSimulation(requestJson: String): String {
        return try {
            val req = json.decodeFromString<BridgeRequest>(requestJson)
            val engine = RewardStrategyEngine()

            // 1. Run the core engine (Now respects userState.ownedCards and UPI networks!)
            val results = engine.evaluate(
                transaction = req.transaction,
                inventory = req.inventory,
                availableRoutes = req.availableRoutes,
                userState = req.userState
            )


            // 2. Map the rich engine output to the JS-friendly Flat object
            val jsResults = results.map { res ->
                FlatSimulationResult(
                    cardId = res.cardId,
                    cardName = res.cardName,
                    routeName = res.routeName,
                    // 🟢 NEW: The single highest INR yield for the UI's primary sorting
                    netValue = res.maxYieldInr,
                    // 🟢 NEW: Math breakdown splits for the receipt UI
                    basePoints = res.basePoints,
                    bonusPoints = res.bonusPoints,
                    pointsEarned = res.rawPointsEarned.toDouble(),
                    // 🟢 NEW: Full valuation array for the "Max Value" dropdown
                    spectrumOptions = res.spectrumOptions.map {
                        JsSpectrumResult(it.label, it.liquidity, it.inrValue, it.redemptionLogic)
                    },
                    rewardCurrencyLabel = res.currencyName,
                    mathBreakdown = res.mathBreakdown,
                    isCapped = res.isCapped,
                    routeMetadata = RouteMetadata(
                        res.routeDescription, res.routeLink
                    )
                )
            }

            // 3. Encode and send back to JS
            json.encodeToString<List<FlatSimulationResult>>(jsResults)
        } catch (e: Exception) {
            // 🟢 Print to the browser console for debugging
            println("RewStack Engine Error: ${e.message}")
            e.printStackTrace()

            // 🟢 Return an empty array so React doesn't crash on .map()
            "[]"
        }
    }
}

// ==========================================
// 🟢 DATA MODELS FOR JS EXPORT
// ==========================================

@Serializable
data class BridgeRequest(
    val transaction: Transaction, // Matches your updated TransactionRequest with 'channel'
    val inventory: List<CreditCard>,
    val availableRoutes: List<RouteDefinition>,
    val userState: UserState = UserState()
)

@Serializable
data class JsSpectrumResult(
    val label: String,
    val liquidity: String,
    val inrValue: Double,
    val redemptionLogic: String
)
@Serializable
data class RouteMetadata(
    val instruction: String?,
    val externalLink: String? = null
)
@Serializable
data class FlatSimulationResult(
    val cardId: String,
    val cardName: String,
    val routeName: String,
    val netValue: Double,
    val basePoints: Double,
    val bonusPoints: Double,
    val pointsEarned: Double,
    val spectrumOptions: List<JsSpectrumResult>,
    val rewardCurrencyLabel: String,
    val mathBreakdown: String,
    val isCapped: Boolean,
    val routeMetadata: RouteMetadata? = null
)